import React, { useState } from 'react';

import { useConfig } from '../../default-config';
import useQueryParam from '../../hooks/useQueryParam';
import {
    getBankregnr,
    getNames,
    isValidEmail,
    isValidFullName,
    isValidPhoneNumber,
    isValidQueryParam,
} from '../../utils/contact-form-utils';
import { contactFormTrackingIDs } from '../../utils/gtm/trackIDs/contact';
import { registerContactType } from '../actions';
import { asyncDispatch } from '../actions/types';
import Input from './Input';
import { StatusPending, StatusError, StatusRegisterSuccess } from './registerStatus';

export interface ContactFormAppProps {
    registerPending: boolean;
    registerSuccess: boolean;
    hasError: boolean;
    resetState: () => asyncDispatch;
    registerContact: registerContactType;
    bankregnrFromSitecore?: string;
}

const DEFAULT_PRODUCT = 'ukjent-produkt';

const ContactForm = ({
    registerPending,
    registerSuccess,
    hasError,
    resetState,
    registerContact,
    bankregnrFromSitecore,
}: ContactFormAppProps) => {
    const { texts, links, bankregnr: brnr } = useConfig();
    const [fullName, setFullName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [contactRequired, setContactRequired] = useState('');
    const [nameRequired, setNameRequired] = useState('');
    const [product] = useQueryParam('product');
    const [company] = useQueryParam('company');
    const bankregnr = getBankregnr(bankregnrFromSitecore, company) ?? brnr;

    const isValidForm = () => {
        if (!isValidFullName(fullName)) {
            setNameRequired(texts.fillName);
            return false;
        }
        const [firstName, lastName] = getNames(fullName);
        if (firstName.length > 50 || lastName.length > 50) {
            setNameRequired(texts.nameTooLong);
            return false;
        }
        if (!isValidEmail(email) && !isValidPhoneNumber(phone)) {
            setContactRequired(texts.fillContact);
            return false;
        }

        return isValidQueryParam(product ?? DEFAULT_PRODUCT);
    };

    const onSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
        event.preventDefault();
        if (isValidForm() && bankregnr) {
            const [firstName, lastName] = getNames(fullName);
            registerContact({
                name: firstName,
                surname: lastName,
                email: isValidEmail(email) ? email : '',
                phone: isValidPhoneNumber(phone) ? phone : '',
                bankregnr: { value: bankregnr },
                productName: product ?? DEFAULT_PRODUCT,
            });
        }
    };

    const resetStateAndForm = () => {
        resetState();
        setFullName('');
        setEmail('');
        setPhone('');
    };

    return (
        <article className="card contact-form">
            <div className="card-content">
                <h2 className="h2">{texts.header}</h2>
                <div className="card-ingress">{texts.ingressText}</div>
                <div className="form-container">
                    <form id="form" className="form" onSubmit={onSubmit}>
                        <Input
                            id="fullname"
                            onChange={(event) => {
                                setFullName(event.target.value);
                                setNameRequired('');
                            }}
                            value={fullName}
                            label={texts.name}
                            errorText={nameRequired}
                        />
                        <Input
                            id="email"
                            type="email"
                            onChange={(event) => {
                                setEmail(event.target.value);
                                setContactRequired('');
                            }}
                            value={email}
                            label={texts.email}
                            errorText={email.length > 100 ? texts.emailTooLong : contactRequired}
                        />
                        <Input
                            id="phone"
                            type="tel"
                            onChange={(event) => {
                                setPhone(event.target.value);
                                setContactRequired('');
                            }}
                            value={phone}
                            label={texts.phone}
                            errorText={phone.length > 50 ? texts.phoneNumberTooLong : contactRequired}
                        />
                        <button
                            className="btn btn-primary"
                            type="submit"
                            data-track-id={contactFormTrackingIDs.private.id}
                            data-track-linkname="submit"
                        >
                            {texts.send}
                        </button>
                    </form>
                    <div className="information-text">
                        {texts.gdprText}
                        <a href={links.gdprURL.url}>{links.gdprURL.text}</a>
                    </div>
                    {registerPending && <StatusPending />}
                    {hasError && <StatusError onClick={() => resetState()} />}
                    {registerSuccess && <StatusRegisterSuccess onClick={resetStateAndForm} />}
                </div>
            </div>
        </article>
    );
};

export default ContactForm;
